import React, { useState } from 'react';
import { LayoutDashboard, FileText, Settings, ChevronDown, ChevronRight, LogOut, User, ShieldCheck, CreditCard, Users, Percent, AlertTriangle, Receipt, List, FileSearch, TrendingUp } from 'lucide-react';
interface SidebarProps {
  currentPage: string;
  onNavigate: (page: string) => void;
  onLogout: () => void;
}
export function Sidebar({
  currentPage,
  onNavigate,
  onLogout
}: SidebarProps) {
  const [isProductMenuOpen, setIsProductMenuOpen] = useState(true);
  const [isApplicationMenuOpen, setIsApplicationMenuOpen] = useState(true);
  const [isAuditMenuOpen, setIsAuditMenuOpen] = useState(true);
  const productSubmenu = [{
    id: 'loan-products',
    label: 'Loan Product Setup',
    icon: Settings
  }, {
    id: 'fee-setup',
    label: 'Fee Setup',
    icon: CreditCard
  }, {
    id: 'kyc-group',
    label: 'KYC Group',
    icon: Users
  }, {
    id: 'interest-setup',
    label: 'Interest/Markup Setup',
    icon: Percent
  }, {
    id: 'penalties-setup',
    label: 'Penalties Setup',
    icon: AlertTriangle
  }, {
    id: 'tax-setup',
    label: 'Tax Setup',
    icon: Receipt
  }, {
    id: 'taxes-on-fees',
    label: 'Taxes on Fees',
    icon: Receipt
  }, {
    id: 'credit-scoring',
    label: 'Credit Scoring',
    icon: TrendingUp
  }];
  const auditSubmenu = [{
    id: 'audit-loan-apps',
    label: 'Loan Application Audit Logs'
  }, {
    id: 'audit-backoffice',
    label: 'Backoffice Audit Logs'
  }];
  return <div className="h-screen w-64 bg-gray-900 text-white flex flex-col fixed left-0 top-0 z-40 shadow-xl" data-id="element-1007">
      {/* Logo Area */}
      <div className="h-16 flex items-center px-6 border-b border-gray-800" data-id="element-1008">
        <ShieldCheck className="h-8 w-8 text-blue-500 mr-3" data-id="element-1009" />
        <div data-id="element-1010">
          <h1 className="text-lg font-bold tracking-tight" data-id="element-1011">Bunyaad Finance</h1>
          <p className="text-xs text-gray-400" data-id="element-1012">LOS System</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-id="element-1013">
        {/* Dashboard */}
        <button onClick={() => onNavigate('dashboard')} className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors ${currentPage === 'dashboard' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-800 hover:text-white'}`} data-id="element-1014">
          <LayoutDashboard className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1015" />
          Dashboard
        </button>

        {/* Application Management */}
        <div className="pt-2" data-id="element-1016">
          <button onClick={() => setIsApplicationMenuOpen(!isApplicationMenuOpen)} className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-800 hover:text-white transition-colors" data-id="element-1017">
            <div className="flex items-center" data-id="element-1018">
              <FileText className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1019" />
              Application Mgmt
            </div>
            {isApplicationMenuOpen ? <ChevronDown className="h-4 w-4" data-id="element-1020" /> : <ChevronRight className="h-4 w-4" data-id="element-1021" />}
          </button>

          {isApplicationMenuOpen && <div className="mt-1 space-y-1" data-id="element-1022">
              <button onClick={() => onNavigate('app-master')} className={`w-full flex items-center pl-11 pr-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === 'app-master' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`} data-id="element-1023">
                <List className="h-4 w-4 mr-2" data-id="element-1024" />
                Loan Applications
              </button>
              <button onClick={() => onNavigate('onboarding-master')} className={`w-full flex items-center pl-11 pr-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === 'onboarding-master' ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`} data-id="element-1025">
                <Users className="h-4 w-4 mr-2" data-id="element-1026" />
                Onboarding Applications
              </button>
            </div>}
        </div>

        {/* Product Management */}
        <div className="pt-2" data-id="element-1027">
          <button onClick={() => setIsProductMenuOpen(!isProductMenuOpen)} className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-800 hover:text-white transition-colors" data-id="element-1028">
            <div className="flex items-center" data-id="element-1029">
              <Settings className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1030" />
              Product Management
            </div>
            {isProductMenuOpen ? <ChevronDown className="h-4 w-4" data-id="element-1031" /> : <ChevronRight className="h-4 w-4" data-id="element-1032" />}
          </button>

          {isProductMenuOpen && <div className="mt-1 space-y-1 pl-10" data-id="element-1033">
              {productSubmenu.map(item => <button key={item.id} onClick={() => onNavigate(item.id)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === item.id ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`} data-id="element-1034">
                  <item.icon className="h-4 w-4 mr-2" data-id="element-1035" />
                  <span className="truncate" data-id="element-1036">{item.label}</span>
                </button>)}
            </div>}
        </div>

        {/* Audit Management */}
        <div className="pt-2" data-id="element-1037">
          <button onClick={() => setIsAuditMenuOpen(!isAuditMenuOpen)} className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-md text-gray-300 hover:bg-gray-800 hover:text-white transition-colors" data-id="element-1038">
            <div className="flex items-center" data-id="element-1039">
              <FileSearch className="h-5 w-5 mr-3 flex-shrink-0" data-id="element-1040" />
              Audit Management
            </div>
            {isAuditMenuOpen ? <ChevronDown className="h-4 w-4" data-id="element-1041" /> : <ChevronRight className="h-4 w-4" data-id="element-1042" />}
          </button>

          {isAuditMenuOpen && <div className="mt-1 space-y-1 pl-10" data-id="element-1043">
              {auditSubmenu.map(item => <button key={item.id} onClick={() => onNavigate(item.id)} className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors ${currentPage === item.id ? 'bg-blue-600/20 text-blue-400' : 'text-gray-400 hover:bg-gray-800 hover:text-white'}`} data-id="element-1044">
                  <span className="truncate text-left" data-id="element-1045">{item.label}</span>
                </button>)}
            </div>}
        </div>
      </nav>

      {/* User Profile */}
      <div className="border-t border-gray-800 p-4" data-id="element-1046">
        <div className="flex items-center justify-between" data-id="element-1047">
          <div className="flex items-center" data-id="element-1048">
            <div className="h-8 w-8 rounded-full bg-gray-700 flex items-center justify-center" data-id="element-1049">
              <User className="h-5 w-5 text-gray-400" data-id="element-1050" />
            </div>
            <div className="ml-3" data-id="element-1051">
              <p className="text-sm font-medium text-white" data-id="element-1052">Admin User</p>
              <p className="text-xs text-gray-500" data-id="element-1053">admin@bunyaadfinance.com</p>
            </div>
          </div>
          <button onClick={onLogout} className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors" title="Sign out" data-id="element-1054">
            <LogOut className="h-5 w-5" data-id="element-1055" />
          </button>
        </div>
      </div>
    </div>;
}